import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import client from "../../api/client";
import { useAuthStore } from "../../store/authStore";
import Spinner from "../../components/Spinner";

const MAX_CHARS = 1000;

type DreamResult = {
  dream: string;
  interpretation: string;
  has_saju: boolean;
};

export default function DreamInterpretation() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [dream, setDream] = useState("");
  const [result, setResult] = useState<DreamResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) {
    navigate("/login");
    return null;
  }

  async function handleSubmit() {
    if (!dream.trim()) {
      setError("꿈 내용을 입력해 주세요.");
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await client.post("/features/dream-interpretation", { dream });
      setResult(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "꿈 해몽을 불러올 수 없습니다.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setResult(null);
    setDream("");
    setError(null);
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">🌙 꿈 해몽</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">사주 오행으로 풀어보는 나의 꿈</p>
          </div>
        </div>

        {/* 입력 폼 */}
        {!result && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
            <p className="text-sm font-bold text-[var(--color-text-primary)] mb-1">어떤 꿈을 꾸셨나요?</p>
            <p className="text-xs text-[var(--color-text-tertiary)] mb-3">
              꿈의 내용을 최대한 구체적으로 적어주세요. 인물, 장소, 행동, 색상, 감정 등을 포함하면 더 정확한 해몽이 가능합니다.
            </p>

            <div className="relative">
              <textarea
                value={dream}
                onChange={(e) => setDream(e.target.value.slice(0, MAX_CHARS))}
                placeholder="예) 하늘을 날다가 갑자기 떨어지는 꿈을 꿨어요. 무서웠지만 착지할 때는 부드럽게 내려앉았고, 주변에 낯선 사람들이 있었어요..."
                rows={6}
                className="w-full border border-[var(--color-border-default)] rounded-xl px-3 py-3 text-sm text-[var(--color-text-primary)] bg-[var(--color-bg)] focus:outline-none focus:border-[var(--color-border-accent)] resize-none"
              />
              <span className="absolute bottom-2 right-3 text-[11px] text-[var(--color-text-disabled)]">
                {dream.length}/{MAX_CHARS}
              </span>
            </div>

            {error && (
              <p className="mt-3 text-xs text-[var(--color-error)] text-center">{error}</p>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading || !dream.trim()}
              className="mt-4 w-full py-3 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity"
            >
              {loading ? "럭키가 살펴보는 중" : "🔮 꿈 해몽하기"}
            </button>
          </div>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="flex flex-col items-center py-16 gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-[var(--color-text-tertiary)]">사주 오행으로 꿈을 해석하고 있습니다...</p>
          </div>
        )}

        {/* 결과 */}
        {result && !loading && (
          <div>
            {/* 꿈 요약 */}
            <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-2xl p-4 mb-4">
              <p className="text-[11px] text-indigo-400 font-bold mb-1">꿈 내용</p>
              <p className="text-sm text-indigo-300 leading-relaxed line-clamp-3">{result.dream}</p>
              {!result.has_saju && (
                <p className="text-[11px] text-indigo-400/70 mt-2">
                  * 사주 정보를 등록하면 더 개인화된 해몽을 받을 수 있습니다.
                </p>
              )}
            </div>

            {/* 해몽 결과 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🌙</span>
                <h2 className="text-base font-bold text-[var(--color-text-primary)]">해몽 결과</h2>
              </div>
              <div className="text-sm text-[var(--color-text-secondary)] leading-relaxed prose prose-sm max-w-none prose-headings:text-[var(--color-text-primary)] prose-headings:font-bold prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1">
                <ReactMarkdown>{result.interpretation}</ReactMarkdown>
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="space-y-2">
              <button
                onClick={() =>
                  navigate("/counseling", {
                    state: { autoStart: true, category: "general", mode: "qna" },
                  })
                }
                className="w-full py-3 bg-[var(--color-surface)] border border-[var(--color-accent)]/30 text-[var(--color-accent)] rounded-xl text-sm font-bold hover:bg-[var(--color-accent)]/10 transition-colors"
              >
                💬 AI 상담사에게 더 물어보기
              </button>
              <button
                onClick={handleReset}
                className="w-full py-3 bg-[var(--color-bg)] border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm font-medium hover:text-[var(--color-text-primary)] transition-colors"
              >
                다른 꿈 해몽하기
              </button>
            </div>

            <p className="text-center text-[var(--color-text-disabled)] text-[11px] mt-4">
              * AI 해석은 참고용입니다. 전문 상담사의 의견과 다를 수 있습니다.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
